import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Zap, BarChart3, Clock, CheckCircle } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  SMART SCHEDULING (Phase 18) — AI-assisted appointment suggestions.
//  3 views:
//    • Suggest — input constraints → ranked slot list + 1-click book
//    • Utilization — per-doctor busy% for a month
//    • Gaps — free windows on a specific day
// ═══════════════════════════════════════════════════════════════════

const SmartSchedulePage = ({ API_BASE }) => {
  const [view, setView] = useState('suggest');

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 16px' }}>⚡ Smart Scheduling</h2>

      <div style={{ ...cardStyle, marginBottom:12, background: theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color: theme.navy, lineHeight:1.6 }}>
          <b>How it works:</b> Engine considers doctor's weekly schedule, existing appointments, treatment duration, and patient preferences — then ranks the best slots. Use Utilization tab to see which doctors are over/under-loaded. Use Gaps tab to find free windows you can fill with walk-ins or follow-ups.
        </div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {[
          ['suggest', '⚡ Suggest Slot'],
          ['utilization', '📊 Doctor Utilization'],
          ['gaps', '🕳 Free Gaps'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} style={{
            padding:'10px 16px', borderRadius:6, cursor:'pointer',
            border:`1px solid ${view===k ? theme.primary : theme.border}`,
            background: view===k ? theme.primary : 'white',
            color: view===k ? 'white' : theme.navy, fontWeight:600,
          }}>{label}</button>
        ))}
      </div>

      {view === 'suggest' && <SuggestView API_BASE={API_BASE}/>}
      {view === 'utilization' && <UtilizationView API_BASE={API_BASE}/>}
      {view === 'gaps' && <GapsView API_BASE={API_BASE}/>}
    </div>
  );
};

// ── Suggest View ──
const SuggestView = ({ API_BASE }) => {
  const [doctors, setDoctors] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({
    patientId: '', treatmentTypeName: '', preferredDoctorId: '',
    startDate: new Date().toISOString().slice(0, 10),
    durationMinutes: '',
    preferredTimeBand: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/doctors`).then(r => setDoctors(r.data || []));
    axios.get(`${API_BASE}/treatment-types`).then(r => setTreatmentTypes(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/patients`).then(r => setPatients((r.data || []).slice(0, 500)));
  }, [API_BASE]);

  const suggest = async () => {
    setLoading(true); setResult(null);
    try {
      const payload = { ...form };
      if (!payload.durationMinutes) delete payload.durationMinutes;
      else payload.durationMinutes = Number(payload.durationMinutes);
      Object.keys(payload).forEach(k => payload[k] === '' && delete payload[k]);
      const r = await axios.post(`${API_BASE}/smart-schedule/suggest`, payload);
      setResult(r.data);
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  };

  const bookSlot = async (s) => {
    if (!form.patientId) return alert('Select a patient first');
    const patient = patients.find(p => p._id === form.patientId);
    try {
      await axios.post(`${API_BASE}/appointments`, {
        patientId: patient._id, patientName: patient.name, phone: patient.phone || '',
        date: s.date, time: s.time, duration: s.durationMin,
        doctorId: s.doctorId, branchId: s.branchId || undefined,
        treatment: form.treatmentTypeName || '',
        status: 'Scheduled',
      });
      setBooking({ date: s.date, time: s.time, doctorName: s.doctorName });
      setResult(null);
    } catch (e) { alert('Book failed: ' + (e.response?.data?.error || e.message)); }
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'360px 1fr', gap:16 }}>
      <div style={cardStyle}>
        <h3 style={{ color: theme.navy, margin:'0 0 12px' }}>Find Best Slot</h3>
        <Label>Patient</Label>
        <select value={form.patientId} onChange={e => setForm({ ...form, patientId: e.target.value })} style={{ ...inputStyle, marginBottom:10 }}>
          <option value="">Select patient…</option>
          {patients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
        </select>

        <Label>Treatment Type</Label>
        <select value={form.treatmentTypeName} onChange={e => setForm({ ...form, treatmentTypeName: e.target.value })} style={{ ...inputStyle, marginBottom:10 }}>
          <option value="">(Any / custom duration)</option>
          {treatmentTypes.map(t => <option key={t._id} value={t.name}>{t.name} ({t.defaultDuration || 30}m)</option>)}
        </select>

        <Label>Duration override (min)</Label>
        <input type="number" placeholder="leave blank for default"
          value={form.durationMinutes} onChange={e => setForm({ ...form, durationMinutes: e.target.value })} style={{ ...inputStyle, marginBottom:10 }}/>

        <Label>Preferred Doctor</Label>
        <select value={form.preferredDoctorId} onChange={e => setForm({ ...form, preferredDoctorId: e.target.value })} style={{ ...inputStyle, marginBottom:10 }}>
          <option value="">Any doctor</option>
          {doctors.map(d => <option key={d._id} value={d._id}>{d.name}{d.specialty ? ` · ${d.specialty}` : ''}</option>)}
        </select>

        <Label>Start From</Label>
        <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} style={{ ...inputStyle, marginBottom:10 }}/>

        <Label>Preferred Time</Label>
        <select value={form.preferredTimeBand} onChange={e => setForm({ ...form, preferredTimeBand: e.target.value })} style={{ ...inputStyle, marginBottom:16 }}>
          <option value="">Any</option>
          <option value="morning">Morning (before 12)</option>
          <option value="afternoon">Afternoon (12-5)</option>
          <option value="evening">Evening (after 5)</option>
        </select>

        <button onClick={suggest} disabled={loading} style={{ ...btnStyle, width:'100%', background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center', justifyContent:'center' }}>
          <Zap size={16}/> {loading ? 'Finding…' : 'Find Best Slots'}
        </button>
      </div>

      <div>
        {booking && (
          <div style={{ ...cardStyle, marginBottom:12, background:'#f0fdf4', borderLeft:`4px solid ${theme.success}` }}>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <CheckCircle size={24} color={theme.success}/>
              <div>
                <div style={{ fontWeight:700, color: theme.navy }}>Booked!</div>
                <div style={{ fontSize:13, color:'#666' }}>{booking.date} at {booking.time} with {booking.doctorName}</div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <>
            <div style={{ marginBottom:10, fontSize:13, color:'#666' }}>
              Considered <b>{result.doctorsConsidered}</b> doctors · Duration <b>{result.durationMin}m</b> · Showing top {result.suggestions.length}
            </div>
            {result.suggestions.length === 0 && (
              <div style={{ ...cardStyle, padding:40, textAlign:'center', color:'#888' }}>
                No slots found. Try widening the date range or changing doctor filter.
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:10 }}>
              {result.suggestions.map((s, i) => (
                <div key={i} style={{ ...cardStyle, padding:14, borderLeft:`4px solid ${i < 3 ? theme.success : theme.border}` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div>
                      <div style={{ fontWeight:700, color: theme.navy, fontSize:16 }}>
                        {new Date(s.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short' })}
                      </div>
                      <div style={{ fontSize:14, color: theme.navy, marginTop:2 }}>
                        {s.time} – {s.endTime}
                      </div>
                      <div style={{ fontSize:12, color:'#666', marginTop:4 }}>
                        {s.doctorName}{s.specialty ? ` · ${s.specialty}` : ''}
                      </div>
                      <div style={{ fontSize:11, color:'#888', marginTop:2, textTransform:'capitalize' }}>
                        {s.timeBand}
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ padding:'3px 8px', borderRadius:10, fontSize:10, fontWeight:700,
                        background: i < 3 ? '#dcfce7' : '#f3f4f6',
                        color: i < 3 ? '#166534' : '#374151' }}>
                        Score {s.score}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => bookSlot(s)} disabled={!form.patientId}
                    style={{ ...btnStyle, width:'100%', marginTop:10, background: theme.success, color:'white', opacity: form.patientId ? 1 : 0.5 }}>
                    Book This Slot
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {!result && !booking && (
          <div style={{ ...cardStyle, padding:40, textAlign:'center', color:'#888' }}>
            Fill the form on the left and click "Find Best Slots" to see AI-ranked suggestions.
          </div>
        )}
      </div>
    </div>
  );
};

// ── Utilization View ──
const UtilizationView = ({ API_BASE }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/smart-schedule/doctor-utilization`, { params: { month } });
      setData(r.data);
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  }, [API_BASE, month]);

  useEffect(() => { load(); }, [load]);

  const monthOptions = () => {
    const out = []; const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      out.push({ value: v, label: d.toLocaleString('en-IN', { month:'short', year:'numeric' }) });
    }
    return out;
  };

  return (
    <>
      <div style={{ marginBottom:12 }}>
        <select value={month} onChange={e => setMonth(e.target.value)} style={{ ...inputStyle, width:180 }}>
          {monthOptions().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        {!data ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>
         : data.rows.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No active doctors</div>
         : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f9fafb' }}>
              <th style={th}>Doctor</th><th style={th}>Specialty</th>
              <th style={th}>Appointments</th><th style={th}>Scheduled Hrs</th>
              <th style={th}>Busy Hrs</th><th style={th}>Utilization</th>
            </tr></thead>
            <tbody>
              {data.rows.map(r => {
                const color = r.utilization >= 80 ? theme.danger : r.utilization >= 50 ? theme.success : r.utilization >= 25 ? theme.warning : '#888';
                return (
                  <tr key={r.doctorId} style={{ borderBottom:'1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontWeight:600 }}>{r.doctorName}</td>
                    <td style={td}>{r.specialty || '—'}</td>
                    <td style={td}>{r.appointments}</td>
                    <td style={td}>{r.scheduledHours}h</td>
                    <td style={td}>{r.busyHours}h</td>
                    <td style={td}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:100, height:8, background:'#f1f3f5', borderRadius:4, overflow:'hidden' }}>
                          <div style={{ width: `${Math.min(100, r.utilization)}%`, height:'100%', background: color }}/>
                        </div>
                        <span style={{ fontWeight:700, color }}>{r.utilization}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
         )}
      </div>
    </>
  );
};

// ── Gaps View ──
const GapsView = ({ API_BASE }) => {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => { axios.get(`${API_BASE}/doctors`).then(r => setDoctors(r.data || [])); }, [API_BASE]);

  const load = useCallback(async () => {
    try {
      const params = { date };
      if (doctorId) params.doctorId = doctorId;
      const r = await axios.get(`${API_BASE}/smart-schedule/gaps`, { params });
      setData(r.data);
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  }, [API_BASE, date, doctorId]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inputStyle, width:160 }}/>
        <select value={doctorId} onChange={e => setDoctorId(e.target.value)} style={{ ...inputStyle, width:220 }}>
          <option value="">All active doctors</option>
          {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
        </select>
      </div>
      {!data ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>
       : data.rows.length === 0 ? <div style={{ ...cardStyle, padding:40, textAlign:'center', color:'#888' }}>No doctors scheduled this day</div>
       : data.rows.map(r => (
        <div key={r.doctorId} style={{ ...cardStyle, marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div>
              <div style={{ fontWeight:700, color: theme.navy }}>{r.doctorName}</div>
              <div style={{ fontSize:12, color:'#666' }}>{r.appointments} appointments · {Math.round(r.totalGapMinutes/60*10)/10}h total free</div>
            </div>
            <div style={{ padding:'4px 12px', borderRadius:12, background:'#dcfce7', color:'#166534', fontSize:12, fontWeight:700 }}>
              {r.gaps.length} gap{r.gaps.length !== 1 ? 's' : ''}
            </div>
          </div>
          {r.gaps.length === 0 ? <div style={{ fontSize:13, color:'#888' }}>Fully booked</div>
           : (
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {r.gaps.map((g, i) => (
                <div key={i} style={{ padding:'8px 12px', border:`1px solid ${theme.border}`, borderRadius:8, background:'#f9fafb' }}>
                  <div style={{ fontWeight:600, color: theme.navy, fontSize:13 }}>{g.start} – {g.end}</div>
                  <div style={{ fontSize:11, color:'#666' }}>{g.minutes}m free</div>
                </div>
              ))}
            </div>
           )}
        </div>
       ))}
    </>
  );
};

const Label = ({ children }) => <div style={{ fontSize:12, color:'#555', fontWeight:600, marginBottom:4 }}>{children}</div>;
const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };

export default SmartSchedulePage;
